/* eslint-disable max-len */
import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

export const metadataNullDefaults = {
  tagline: null,
  startDate: null,
  endDate: null,
  categories: null,
};

/**
 * logging util function that respects debug mode of experience
 * @param {Error} error
 */
const logWarning = (error) => {
  if (typeof window !== 'undefined' && window.__EXPERIENCE_CONTEXT__?.isDebugMode) {
    console.warn(error.message); // eslint-disable-line no-console
  }
};

/**
 * Removes duplicate products, where equivalence is itemId equality
 * @param {Array.Object} products non-null
 * @returns products deduped
 */
const getUniqueProducts = (products) => {
  const itemIdProductMap = products.reduce((uniques, product) => {
    const itemId = product?.itemId;
    if (itemId && !uniques.has(itemId)) uniques.set(itemId, product);
    return uniques;
  }, new Map());

  return Array.from(itemIdProductMap.values());
};

/**
 * @param {Object} rawProduct non-null
 * @param {Function} pushError
 * @returns nullable product object
 */
const productBuilder = (rawProduct, pushError) => {
  if (!rawProduct || typeof rawProduct !== 'object') {
    pushError(new Error('productBuilder: unparseable product'));
    return null;
  }

  if (!rawProduct.currentPrice) {
    pushError(new Error('productBuilder: no currentPrice'));
    return null;
  }

  const transformedProduct = {
    itemId: typeof rawProduct.itemId === 'number'
      ? rawProduct.itemId
      : pushError(new Error('productBuilder: unparseable itemId')),

    inventoryAvailable: typeof rawProduct.inventoryAvailable === 'boolean'
      ? rawProduct.inventoryAvailable
      : pushError(new Error('productBuilder: unparseable inventoryAvailable')),

    thumbnail: typeof rawProduct.size100ImageUrlList?.[0] === 'string'
      ? rawProduct.size100ImageUrlList[0]
      : pushError(new Error('productBuilder: unparseable thumbnail')),

    image: typeof rawProduct.size400ImageUrlList?.[0] === 'string'
      ? rawProduct.size400ImageUrlList[0]
      : pushError(new Error('productBuilder: unparseable image')),

    specialBuyType: ['sbotd', 'decor', 'pro', undefined, null].includes(rawProduct.specialBuyType)
      ? rawProduct.specialBuyType || null // coerce undefined to null per contract
      : pushError(new Error('productBuilder: unparseable specialBuyType')),

    currentPrice: typeof rawProduct.currentPrice === 'string'
      ? rawProduct.currentPrice
      : pushError(new Error('productBuilder: unparseable currentPrice')),

    hasSwatches: Boolean(rawProduct.info?.swatches?.length),
  };

  return transformedProduct;
};

/**
 * @param {Array.Object} rawProducts non-null
 * @param {Function} pushError
 * @returns nullable array of non-null products
 */
const productsBuilder = (rawProducts, pushError) => {
  if (!Array.isArray(rawProducts)) {
    pushError(new Error('productsBuilder: unparseable products list'));
    return null;
  }

  const specialBuyProducts = [];
  getUniqueProducts(rawProducts).forEach((product) => {
    const newProduct = productBuilder(product, pushError);
    if (newProduct) specialBuyProducts.push(newProduct);
  });

  return specialBuyProducts;
};

/**
 * @param {Object} category non-null
 * @param {Function} pushError
 * @returns nullable category object
 */
const categoryBuilder = (rawCategory, pushError) => {
  if (!rawCategory || typeof rawCategory !== 'object') {
    pushError(new Error('categoryBuilder: unparseable category'));
    return null;
  }

  const products = productsBuilder(rawCategory.specialBuyProductList, pushError);

  const transformedCategory = {
    name: typeof rawCategory.category === 'string'
      ? rawCategory.category
      : pushError(new Error('categoryBuilder: unparseable name')),

    tagline: typeof rawCategory.categoryTagLine === 'string'
      ? rawCategory.categoryTagLine
      : pushError(new Error('categoryBuilder: unparseable tagline')),

    mobileTagline: typeof rawCategory.mobileCategoryTagline === 'string'
      ? rawCategory.mobileCategoryTagline
      : pushError(new Error('categoryBuilder: unparseable mobileTagline')),

    products: products?.length
      ? products
      : pushError(new Error('categoryBuilder: no products')),

    categoryId: typeof rawCategory.categoryId === 'string'
      ? rawCategory.categoryId
      : pushError(new Error('categoryBuilder: unparseable categoryId')),
  };

  return transformedCategory;
};

/**
 * @param {Array.Object} rawCategories non-null
 * @param {Function} pushError
 * @returns nullable array of non-null categories
 */
const categoriesBuilder = (rawCategories, pushError) => {
  if (!Array.isArray(rawCategories)) {
    pushError(new Error('categoriesBuilder: unparseable categories'));
    return null;
  }

  const specialBuyCategories = [];
  rawCategories.forEach((rawCategory) => {
    const newCategory = categoryBuilder(rawCategory, pushError);
    if (newCategory) specialBuyCategories.push(newCategory);
  });

  return specialBuyCategories;
};

export const useSpecialBuyApi = (specialBuyType) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const errors = useRef([]);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  const setSpecialBuy = (specialBuy) => { if (isMounted.current) setData(specialBuy.data); };
  const removeLoading = () => { if (isMounted.current) setLoading(false); };

  const pushError = (newError) => {
    logWarning(newError);
    if (isMounted.current) errors.current.push(newError);
    return null;
  };

  const useNullDefaults = (err) => {
    pushError(err);
    return {
      data: metadataNullDefaults
    };
  };

  /**
   * @param {*} resData
   * @returns non-null specialBuy response
   */
  const specialBuyBuilder = (resData) => {
    const specialBuyRaw = JSON.parse(resData)?.[0];
    if (!specialBuyRaw?.specialBuyMetaData) return metadataNullDefaults;

    const categories = categoriesBuilder(specialBuyRaw.specialBuyCategoryProduct, pushError);

    const newSpecialBuy = {
      tagline: typeof specialBuyRaw.specialBuyMetaData.tagline === 'string'
        ? specialBuyRaw.specialBuyMetaData.tagline
        : pushError(new Error('specialBuyBuilder: unparseable tagline')),

      startDate: typeof specialBuyRaw.specialBuyMetaData.startDate === 'string'
        ? specialBuyRaw.specialBuyMetaData.startDate
        : pushError(new Error('specialBuyBuilder: unparseable startDate')),

      endDate: typeof specialBuyRaw.specialBuyMetaData.endDate === 'string'
        ? specialBuyRaw.specialBuyMetaData.endDate
        : pushError(new Error('specialBuyBuilder: unparseable endDate')),

      categories: categories?.length
        ? categories
        : pushError(new Error('specialBuyBuilder: no categories')),
    };

    return newSpecialBuy;
  };

  /**
   * @returns {Promise} resolves to a specialBuy
   */
  const fetchSpecialBuyData = () => {
    let url = '';
    if (specialBuyType === 'sbotd') url = '/SpecialBuy/v2/sbotd-svcs/';
    else if (specialBuyType === 'decor') url = '/SpecialBuy/v2/sbotw-svcs/decor';
    else if (specialBuyType === 'pro') url = '/SpecialBuy/v2/sbotw-svcs/pro';
    else return Promise.reject(new Error('fetchSpecialBuyData: unparseable specialBuyType'));

    return axios({
      url,
      params: { show: 'swatches,category' },
      transformResponse: specialBuyBuilder,
    });
  };

  if (!loading && !data) {
    errors.current = [];
    setLoading(true);
    fetchSpecialBuyData(specialBuyType)
      .catch(useNullDefaults)
      .then(setSpecialBuy)
      .finally(removeLoading);
  }

  return useMemo(() => ({
    data,
    loading,
    // errors is a ref instead of state, so consumers receive snapshots when they receive new data
    // or loading status. This prevents rerender. We toString the Errors instead of handing out a reference
    error: errors.current.length
      ? errors.current.map((error) => error.toString())
      : null
  }), [data, loading]);
};
